import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const COLLS = [
  { key: "c1", name: "Metaland Wolfpack Exclusive", standin: "/art/c1_exclusive.webp" },
  { key: "c2", name: "Metaland Wolfpack (777)", standin: "/art/frost.webp" },
  { key: "c3", name: "Project PUPS", standin: "/art/c3_pups.webp" },
];

function Mini({ t, standin }) {
  return (
    <div className="rounded-xl overflow-hidden border border-slate-700/70 bg-[#061016]" data-testid={`profile-nft-${t.key}-${t.token_id}`}>
      <img src={t.image} alt={t.name} loading="lazy" className="w-full aspect-square object-cover bg-slate-900"
        onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = "1"; e.currentTarget.src = standin; } }} />
      <p className="px-2 py-1.5 text-xs text-slate-200 truncate">{t.name}</p>
    </div>
  );
}

function Minted({ l }) {
  return (
    <a href={l.nft.explorer} target="_blank" rel="noreferrer" data-testid={`profile-nft-sol-${l.id}`}
      className="block rounded-xl overflow-hidden border border-amber-400/40 bg-[#061016] hover:border-amber-300 transition-colors">
      <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image}`} alt={l.name} loading="lazy" className="w-full aspect-square object-cover bg-slate-900" />
      <p className="px-2 py-1.5 text-xs text-slate-200 truncate">#{l.no} · {l.name}</p>
    </a>
  );
}

function Totals({ minted, counts }) {
  const parts = [minted, counts?.c1 || 0, counts?.c2 || 0, counts?.c3 || 0];
  const total = parts.reduce((a, b) => a + b, 0), colls = parts.filter((n) => n > 0).length;
  return (
    <div data-testid="profile-collections-total" className="mt-3 rounded-xl border border-amber-400/30 bg-amber-500/5 px-4 py-3 text-sm text-slate-300">
      {total ? <>You hold <b className="font-mono-game text-amber-200 text-base">{total.toLocaleString()}</b> NFT{total === 1 ? "" : "s"} across <b className="font-mono-game text-amber-200 text-base">{colls}</b> of 4 collections.</>
        : <>You don't hold any Wolfpack NFTs yet. <Link to="/nfts" className="text-cyan-300 hover:underline">See where to get them</Link>.</>}
    </div>
  );
}

function MintedOnes({ onCount }) {
  const [minted, setMinted] = useState(null);
  useEffect(() => {
    const load = () => api.get("/looks/unique/mine").then(({ data }) => { const m = data.looks.filter((l) => l.nft?.status === "complete"); setMinted(m); onCount(m.length); }).catch(() => setMinted([]));
    load();
    window.addEventListener("unique-looks-changed", load);
    return () => window.removeEventListener("unique-looks-changed", load);
  }, []);
  if (!minted) return null;
  return (
    <div data-testid="profile-coll-sol">
      <p className="text-sm text-slate-300 mb-2">Wolfpack Games 1/1 Wolves <span className="font-mono-game text-amber-200 ml-1" data-testid="profile-coll-sol-count">{minted.length}</span></p>
      {minted.length ? <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-3">{minted.map((l) => <Minted key={l.id} l={l} />)}</div>
        : <p className="text-xs text-slate-500">No minted 1/1 wolves yet. Claim one from your 1/1 wolves to see it here.</p>}
    </div>
  );
}

export default function MyCollections({ wallet, counts }) {
  const [items, setItems] = useState(null);
  const [minted, setMinted] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    if (!wallet) return;
    let tries = 0, timer;
    const run = async () => {
      const { data } = await api.get("/nft/my-pack").catch(() => ({ data: { items: [] } }));
      setItems(data.items);
      if (data.items.some((x) => x.pending) && ++tries < 6) timer = setTimeout(run, 4000);
    };
    run();
    return () => clearTimeout(timer);
  }, [wallet]);

  return (
    <section className="glass rounded-2xl p-6" data-testid="profile-collections">
      <h2 className="font-display text-xl text-amber-100 mb-1">My NFT collections</h2>
      {minted !== null && <Totals minted={minted} counts={wallet ? counts : null} />}
      <div className="mt-5 mb-6"><MintedOnes onCount={setMinted} /></div>
      {!wallet ? (
        <p className="text-sm text-slate-400" data-testid="profile-collections-no-wallet">Link your Polygon wallet on the <Link to="/wallet" className="text-cyan-300 hover:underline">NFT Boosts</Link> page to show your Exclusive, Wolfpack and PUPS NFTs here.</p>
      ) : !items ? <p className="text-sm text-slate-500">Loading your NFTs…</p> : (
        <div className="space-y-6">
          {COLLS.map((c) => {
            const mine = items.filter((t) => t.key === c.key);
            return (
              <div key={c.key} data-testid={`profile-coll-${c.key}`}>
                <p className="text-sm text-slate-300 mb-2">{c.name} <span className="font-mono-game text-amber-200 ml-1" data-testid={`profile-coll-${c.key}-count`}>{mine.length}</span></p>
                {mine.length ? (
                  <>
                    <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-3">{(open[c.key] ? mine : mine.slice(0, 12)).map((t) => <Mini key={t.token_id} t={t} standin={c.standin} />)}</div>
                    {mine.length > 12 && (
                      <button data-testid={`profile-coll-${c.key}-toggle`} onClick={() => setOpen({ ...open, [c.key]: !open[c.key] })} className="mt-3 text-xs text-cyan-300 hover:underline">
                        {open[c.key] ? "Show less" : `Show all ${mine.length}`}
                      </button>
                    )}
                  </>
                ) : <p className="text-xs text-slate-500">None in your wallet yet.</p>}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
